import { Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, Sparkles, Shield, FileText, Globe2, Lock, Users, Calendar, RotateCcw, Database, Cpu, Search } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";

const ITEMS = [
  {
    num: 1,
    icon: FileText,
    color: "blue",
    titulo: "Política de tratamiento (fecha y versión)",
    obligacion: "Publica tu política con fecha de vigencia y número de versión. Cada cambio material debe quedar registrado.",
    practica: "Vigencia visible al pie del documento, historial de versiones disponible bajo solicitud.",
  },
  {
    num: 2,
    icon: Users,
    color: "indigo",
    titulo: "Identificación del Responsable + Representante legal + Encargado de Prevención",
    obligacion: "Razón social, RUT, domicilio del Responsable del Tratamiento. Si tienes Encargado de Prevención (Art. 49 modelo prevención), indicar también.",
    practica: "DPO (Art. 50) ≠ Encargado de Prevención (Art. 49). El primero protege datos, el segundo cubre prevención de delitos económicos.",
  },
  {
    num: 3,
    icon: Globe2,
    color: "sky",
    titulo: "Domicilio postal, email, formulario de contacto",
    obligacion: "Canal claro y accesible para notificaciones formales del titular y de la APDP. Email dedicado (privacidad@) + formulario web + dirección postal.",
    practica: "El canal debe estar visible en cada página, no oculto en una sub-página.",
  },
  {
    num: 4,
    icon: Database,
    color: "violet",
    titulo: "Categorías de datos, titulares, destinatarios, finalidades y base de legitimidad",
    obligacion: "Describir qué datos tratas, de quién, a quién se ceden o comunican, para qué fines y sobre qué base legal (consentimiento, contrato, obligación legal, interés legítimo). Si es interés legítimo, declarar cuál.",
    practica: "Estructura tipo tabla. Una fila por finalidad facilita auditoría.",
  },
  {
    num: 5,
    icon: Lock,
    color: "amber",
    titulo: "Política y medidas de seguridad",
    obligacion: "Detalla las medidas técnicas y organizativas. Se recomienda alinear con el Decreto N° 7/2023 del Min. Segpres (Norma Técnica de Seguridad de la Información y Ciberseguridad, Ley 21.180).",
    practica: "Cifrado en tránsito, control de accesos, gestión de incidentes, capacitación y revisiones periódicas.",
  },
  {
    num: 6,
    icon: Shield,
    color: "emerald",
    titulo: "Derechos del titular (ARCO + Portabilidad)",
    obligacion: "Acceso, Rectificación, Cancelación, Oposición + Portabilidad. Plazo máximo de respuesta: 30 días corridos.",
    practica: "Habilita un canal específico (mailto o formulario) e indícalo en la política.",
  },
  {
    num: 7,
    icon: Sparkles,
    color: "orange",
    titulo: "Derecho a recurrir ante la Agencia (APDP)",
    obligacion: "Si el responsable rechaza o no responde oportunamente, el titular puede acudir directamente a la Agencia de Protección de Datos Personales.",
    practica: "Independiente de los derechos ARCO. Indícalo expresamente.",
  },
  {
    num: 8,
    icon: Globe2,
    color: "blue",
    titulo: "Transferencias internacionales (con nivel adecuado o garantías)",
    obligacion: "Declarar si hay transferencia a un tercer país u organización internacional. Si el país NO tiene nivel adecuado, indicar las garantías (típicamente Cláusulas Contractuales Modelo aprobadas por el Min. Economía, RAEX 2025-03731 dic 2025).",
    practica: "AWS USA, Stripe, Mailchimp y la mayoría de los SaaS yanquis requieren las CCM Min. Economía.",
    proCta: { label: "Generador de Cláusulas Contractuales Modelo", to: "/modulo-pro" },
  },
  {
    num: 9,
    icon: Calendar,
    color: "rose",
    titulo: "Período de conservación de los datos",
    obligacion: "Plazo específico por cada categoría de dato + condiciones que fijan ese período (legal, contractual, justificación).",
    practica: "Una tabla con 'categoría / plazo / criterio' es lo que la APDP espera ver.",
  },
  {
    num: 10,
    icon: Search,
    color: "slate",
    titulo: "Fuente de los datos personales",
    obligacion: "Indicar si los datos provienen del titular directamente o de fuentes de acceso público (registros públicos, prensa, redes sociales abiertas).",
    practica: "Si los obtienes de un broker de datos o lista comprada, la base legal de interés legítimo difícilmente se sostiene.",
  },
  {
    num: 11,
    icon: RotateCcw,
    color: "teal",
    titulo: "Derecho a retirar el consentimiento",
    obligacion: "Cuando el tratamiento se base en consentimiento, debe ser tan fácil retirarlo como darlo. El retiro no afecta lo ya tratado lícitamente.",
    practica: "Botón 'Cancelar suscripción' en cada email + opción 'Eliminar mi cuenta' en el perfil.",
  },
  {
    num: 12,
    icon: Cpu,
    color: "fuchsia",
    titulo: "Decisiones automatizadas y elaboración de perfiles",
    obligacion: "Si tu sitio usa chatbot IA, A/B testing, scoring crediticio, recommendation engines o modelos ML que influyan en el titular, debes informar: (a) que existen, (b) la lógica aplicada, (c) las consecuencias previstas, (d) cómo oponerse o pedir intervención humana.",
    practica: "Aplica a Intercom, Drift, Optimizely, Algolia, scoring de fraude (Sift), ranking algorítmico, etc.",
    proCta: { label: "Cláusula de Decisiones Automatizadas", to: "/modulo-pro" },
  },
];

const COLOR_STYLES = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200" },
  sky: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200" },
  slate: { bg: "bg-slate-100", text: "text-slate-700", ring: "ring-slate-200" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-200" },
  fuchsia: { bg: "bg-fuchsia-50", text: "text-fuchsia-700", ring: "ring-fuchsia-200" },
};

export default function Art14Ter() {
  return (
    <>
      <SEO
        title="Art. 14 ter Ley 21.719 — Checklist completo de los 12 ítems obligatorios"
        description="Los 12 requisitos exactos del Art. 14 ter de la Ley 21.719 que tu sitio debe publicar: responsable, datos, derechos ARCO, transferencias internacionales con Cláusulas Min. Economía, decisiones automatizadas. Guía práctica."
        path="/cumplimiento-art-14-ter"
        keywords="Art. 14 ter Ley 21.719, checklist deber de información, transferencias internacionales Chile, Cláusulas Contractuales Modelo Min Economía, decisiones automatizadas, RAEX 2025-03731, Decreto 7 2023 Segpres"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Art. 14 ter Ley 21.719", path: "/cumplimiento-art-14-ter" },
          ]),
        ]}
      />

      <section className="px-6 pt-16 pb-12 sm:pt-20 sm:pb-16 border-b border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full mb-5" data-testid="art14-eyebrow">
            <Shield className="w-3.5 h-3.5" />
            Checklist oficial Ley 21.719
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight">
            Los <span className="text-blue-700">12 ítems</span> del Art. 14 ter que tu sitio debe publicar
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl">
            El Art. 14 ter de la <strong>Ley N° 21.719</strong> establece el «deber de información y transparencia» del responsable. Vigente desde el <strong>1 de diciembre de 2026</strong>. Esta guía recoge la <a href="https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline hover:text-blue-800">Guía Práctica de la Secretaría de Gobierno Digital</a> (Min. Segpres, dic 2025), adaptada a PYMEs del sector privado.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/"
              data-testid="art14-cta-scan"
              className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-semibold px-5 py-3 rounded-full hover:bg-slate-800 transition-colors"
            >
              <Search className="w-4 h-4" />
              Escanea tu sitio gratis
            </Link>
            <Link
              to="/modulo-pro"
              data-testid="art14-cta-pro"
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 text-sm font-semibold px-5 py-3 rounded-full border border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Generar los 12 ítems con Pro
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-8">
            Checklist completo
          </h2>
          <div className="space-y-4">
            {ITEMS.map((it) => {
              const Icon = it.icon;
              const c = COLOR_STYLES[it.color] || COLOR_STYLES.blue;
              return (
                <article
                  key={it.num}
                  data-testid={`art14-item-${it.num}`}
                  className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className={`shrink-0 w-12 h-12 rounded-xl ${c.bg} ${c.text} ring-1 ${c.ring} flex items-center justify-center font-bold text-lg`}>
                      {it.num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className={`w-4 h-4 ${c.text}`} strokeWidth={2} />
                        <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900">
                          {it.titulo}
                        </h3>
                      </div>
                      <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
                        <span className="font-semibold text-slate-900">Obligación: </span>
                        {it.obligacion}
                      </p>
                      <p className="mt-2 text-xs sm:text-sm text-slate-500 italic leading-relaxed">
                        Práctica recomendada: {it.practica}
                      </p>
                      {it.proCta && (
                        <Link
                          to={it.proCta.to}
                          data-testid={`art14-pro-cta-${it.num}`}
                          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {it.proCta.label} →
                        </Link>
                      )}
                    </div>
                    <CheckCircle2 className="hidden sm:block shrink-0 w-5 h-5 text-emerald-500 mt-2" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-6">
            Cómo cumplir los 12 ítems con Ley de Datos Chile
          </h2>
          <ol className="space-y-4 text-slate-700 text-base leading-relaxed list-decimal pl-5">
            <li>
              Ejecuta el <Link to="/" className="text-blue-700 underline">escáner gratuito</Link> de tu sitio para ver qué ítems del Art. 14 ter ya cumples.
            </li>
            <li>
              Activa el <Link to="/modulo-pro" className="text-blue-700 underline">Módulo Pro</Link> para generar tu Política de Privacidad con los 12 ítems exactos, las <strong>Cláusulas Contractuales Modelo</strong> para tus proveedores extranjeros y el <strong>Catálogo de Datos Personales (RAT)</strong>.
            </li>
            <li>
              Publica los documentos en tu sitio y prepara el canal de derechos ARCO (mailto: privacidad@tu-empresa.cl + formulario web).
            </li>
            <li>
              Re-escanea con el <strong>Deep Scan Pro</strong> para validar que las 5 páginas críticas (home, política, cookies, contacto, checkout) cumplen.
            </li>
          </ol>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["Art. 14 ter — Ley 21.719 en BCN", "https://www.bcn.cl/leychile/navegar?idNorma=141599&idParte=10528059&idVersion=2026-12-01"],
              ["Guía Práctica Min. Segpres (Dic. 2025)", "https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales"],
              ["RAEX 2025-03731 — Cláusulas Contractuales Modelo Min. Economía", "https://www.economia.gob.cl/2025/12/10/raex202503731-aprueba-las-clausulas-contractuales-modelo-para-transferencias-internacionales-que-indica.htm"],
              ["Decreto N° 7/2023 — Norma Técnica de Seguridad Min. Segpres", "https://www.bcn.cl/leychile/navegar?idNorma=1195120&idVersion=2023-08-17"],
            ].map(([label, url]) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-colors group"
              >
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-slate-800 group-hover:text-blue-700">{label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
